// react
import { useCallback, useEffect, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Card } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// relative
import { useSynchronizedCharts } from '../../hooks/useSynchronizedCharts';
import ActivationScheduleChart from './ActivationScheduleChart';
import Co2Chart from './Co2Chart';
import ComfortTempChart from './ComfortTempChart';
import { darkTheme, lightTheme } from './highchartsTheme';
import SystemTempChart from './SystemTempChart';

import Highcharts from 'highcharts';
import 'highcharts/modules/no-data-to-display';

// A tiny helper to make the whole card clickable
const CardClickable = ({ id, children, toggle }) => (
  <Card
    onDoubleClick={() => toggle(id)}
    sx={{
      p: 1,
      cursor: 'pointer',
      // transition: 'transition all 300ms ease',
      ':hover': {
        borderColor: 'divider',
      },
      // transform: isExpanded(id) ? 'scale(1.01)' : 'none',
    }}
  >
    {children}
  </Card>
);

export default function SynchronizedHeatingCharts({
  heatingPipeTemp,
  coldWaterTemp,
  comfortTemps,
  co2Series,
  powerConsumptionSeries,
  waterConsumptionSeries,
  relativeHumiditySeries,
  activationRanges,
  baselineRanges,
  occupancyRanges,
  xAxisRange,
  loading = false,
}) {
  const muiTheme = useTheme(); // or receive a prop: themeMode = 'light' | 'dark'
  const mode = muiTheme.palette.mode; // 'light' | 'dark'
  const { t } = useTranslation();
  const { containerRef, syncExtremes } = useSynchronizedCharts(mode);

  // NEW: which chart is expanded?
  const [expanded, setExpanded] = useState(null); // 'activation'|'heating'|'cold'|'comfort'|'co2'|null
  const isExpanded = useCallback((id) => expanded === id, [expanded]);
  const toggle = useCallback((id) => {
    setExpanded((curr) => (curr === id ? null : id));
  }, []);

  useEffect(() => {
    const theme = mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(theme);

    Highcharts.charts.forEach((c) => {
      if (!c) return;
      c.update(
        {
          chart: theme.chart,
          title: theme.title,
          xAxis: theme.xAxis,
          yAxis: theme.yAxis,
          legend: theme.legend,
          tooltip: theme.tooltip,
          plotOptions: theme.plotOptions,
        },
        false,
        true,
        false,
      );
      c.redraw();
      c.reflow();
    });
  }, [mode]);

  const commonXAxis = {
    type: 'datetime',
    min: xAxisRange.min,
    max: xAxisRange.max,
    crosshair: true,
    ordinal: false,
    events: { setExtremes: syncExtremes },
  };

  const h = (id) => (isExpanded(id) ? 400 : 200);

  return (
    <div
      ref={containerRef}
      id="sync-charts-container"
      style={{ gap: '20px', display: 'flex', flexDirection: 'column' }}
    >
      {activationRanges.length > 0 && (
        <CardClickable id="activation" toggle={toggle}>
          <ActivationScheduleChart
            activationRanges={activationRanges}
            baselineRanges={baselineRanges}
            occupancyRanges={occupancyRanges}
            xAxis={commonXAxis}
            height={h('activation')}
            loading={loading}
          />
        </CardClickable>
      )}

      {heatingPipeTemp.length > 0 && (
        <CardClickable id="heating" toggle={toggle}>
          <SystemTempChart
            title={t('buildingOptimization:tooltips.heatingPipeTemperatures')}
            data={heatingPipeTemp}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('heating')}
            loading={loading}
          />
        </CardClickable>
      )}

      {coldWaterTemp.length > 0 && (
        <CardClickable id="cold" toggle={toggle}>
          <SystemTempChart
            title={t('buildingOptimization:tooltips.coldWaterTemperatures')}
            data={coldWaterTemp}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('cold')}
            loading={loading}
          />
        </CardClickable>
      )}

      {comfortTemps.length > 0 && (
        <CardClickable id="comfort" toggle={toggle}>
          <ComfortTempChart
            data={comfortTemps}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('comfort')}
            loading={loading}
          />
        </CardClickable>
      )}

      {co2Series.length > 0 && (
        <CardClickable id="co2" toggle={toggle}>
          <Co2Chart
            data={co2Series}
            xAxis={commonXAxis}
            height={h('co2')}
            loading={loading}
          />
        </CardClickable>
      )}

      {powerConsumptionSeries && powerConsumptionSeries.length > 0 && (
        <CardClickable id="power" toggle={toggle}>
          <SystemTempChart
            title={t('buildingOptimization:tooltips.powerConsumption')}
            data={powerConsumptionSeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('power')}
            loading={loading}
          />
        </CardClickable>
      )}

      {relativeHumiditySeries && relativeHumiditySeries.length > 0 && (
        <CardClickable id="humidity" toggle={toggle}>
          <SystemTempChart
            title={t('buildingOptimization:tooltips.relativeHumidity')}
            data={relativeHumiditySeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('humidity')}
            loading={loading}
          />
        </CardClickable>
      )}

      {waterConsumptionSeries && waterConsumptionSeries.length > 0 && (
        <CardClickable id="water" toggle={toggle}>
          <SystemTempChart
            title={t('buildingOptimization:tooltips.waterConsumption')}
            data={waterConsumptionSeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('water')}
            loading={loading}
          />
        </CardClickable>
      )}
    </div>
  );
}
